import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import QuoteFlowDelegate, { QuoteFlowDomainParams } from 'store/flows/quote/quoteFlowDelegate';
import QuoteProposalInfoDelegate from '../quoteProposalInfoDelegate';
import { NEDDQuoteProposalInfoFormDelegate } from './proposalFormDelegate';

export default class NEDDQuoteProposalInfoPageStore {
  public readonly proposalInfoDelegate: QuoteProposalInfoDelegate;
  public proposalFormDelegate: NEDDQuoteProposalInfoFormDelegate;

  public get flowDelegate(): QuoteFlowDelegate {
    return this.proposalInfoDelegate.flowDelegate;
  }

  public get proposalForm(): NEDDQuoteProposalInfoFormDelegate['proposalForm'] {
    return this.proposalFormDelegate.proposalForm;
  }

  public get isActionLoading(): boolean {
    return this.proposalInfoDelegate.isActionLoading;
  }

  public get isStepCompleted(): boolean {
    return this.proposalInfoDelegate.isStepCompleted;
  }

  public get canCompleteStep(): boolean {
    return this.proposalFormDelegate.isValid && this.proposalInfoDelegate.isFlowReadyToComplete;
  }

  public async getInitialData(): Promise<void> {
    const cartModule = this.flowDelegate.cartModule;

    this.flowDelegate.setIsInitialDataLoading(true);
    await this.proposalInfoDelegate.getInitialData();

    runInAction(() => {
      const cartName = cartModule.cart.cartName;
      const { effectiveDate } = cartModule.lastUpdateBrowsingCriteria;
      const { zip, state, county } = this.proposalInfoDelegate.getLocationFromCartOrGroup();
      this.proposalForm.populateFields({
        proposalName: cartName,
        effectiveDate,
        zip,
        state,
        county,
      });
      this.flowDelegate.setIsInitialDataLoading(false);
    });
  }

  public async onComplete(): Promise<void> {
    const { proposalName, effectiveDate, zip, state, county } = this.proposalForm.getFormDataObject() as Record<
      string,
      string
    >;

    await this.proposalInfoDelegate.saveProposalInfo(proposalName, {
      ...this.flowDelegate.cartModule.lastUpdateBrowsingCriteria,
      effectiveDate,
      zipCode: zip,
      state,
      county,
    });
  }

  constructor(params: QuoteFlowDomainParams) {
    this.proposalInfoDelegate = new QuoteProposalInfoDelegate(params);
    this.proposalFormDelegate = new NEDDQuoteProposalInfoFormDelegate(
      this.proposalInfoDelegate.domainProductLinesDelegate,
    );

    makeAutoObservable(this, { proposalInfoDelegate: false, proposalFormDelegate: false }, { autoBind: true });
  }
}

const NEDDQuoteProposalInfoPageStoreContext = createContext(
  new NEDDQuoteProposalInfoPageStore({} as QuoteFlowDomainParams),
);

export const useNEDDQuoteProposalInfoPageStore = (): NEDDQuoteProposalInfoPageStore => {
  return useContext(NEDDQuoteProposalInfoPageStoreContext);
};

export { NEDDQuoteProposalInfoPageStore, NEDDQuoteProposalInfoPageStoreContext };
